import dayjs from 'dayjs';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { TimePresetMap } from '../../app/constants/time-selection-form';

import { useTabManager } from './tab-manager-context';

import {
  AnomalyRange,
  CRTagData,
  Mode,
  TagWithSortOrder,
  TimePresetKeys,
  TimeRanges,
  TimeSelection,
  TimeSeries,
  ViewType,
} from '@controlrooms/models';

export type IselectedTag = {
  tag: TagWithSortOrder;
  color: string;
};

export type TagDataPoint = {
  [tag: string]: number | string;
};

export type DotsLockPosition = {
  [key: string]: { x: number; y: number };
};

export type tagChartData = { [tagName: string]: CRTagData<TimeSeries, Mode, AnomalyRange> };
export interface InvestigateContextProps {
  selectedTags: { [tagName: string]: IselectedTag };
  setSelectedTags: Dispatch<SetStateAction<{ [tagName: string]: IselectedTag }>>;
  tagChartData: tagChartData;
  setTagChartData: Dispatch<SetStateAction<tagChartData>>;
  investigateTimeSelection: TimeSelection;
  setInvestigateTimeSelection: Dispatch<SetStateAction<TimeSelection>>;
  hoveredTagDatapoints: TagDataPoint;
  setHoveredTagDatapoints: Dispatch<SetStateAction<TagDataPoint>>;
  hoverLockPosition: number | null;
  setHoverLockPosition: Dispatch<SetStateAction<number | null>>;
  highlightedTag: string | null;
  setHighlightedTag: Dispatch<SetStateAction<string | null>>;
  lockedTag: string | null;
  setLockedTag: Dispatch<SetStateAction<string | null>>;
}

const defaultTimeSelections: TimeSelection = {
  startTime: dayjs().subtract(12, 'hour'),
  endTime: dayjs(),
  timezone: dayjs.tz.guess(), // Default to user timezone
  timeRange: TimeRanges.PRESET,
  streamingTimeInSeconds: TimePresetMap.get(TimePresetKeys.LAST_TWELVE_HOURS),
  nowSelected: false,
};

const defaultState = {
  selectedTags: {},
  setSelectedTags: () => null,
  tagChartData: {},
  setTagChartData: () => null,
  hoveredTagDatapoints: {},
  setHoveredTagDatapoints: () => null,
  hoverLockPosition: null,
  setHoverLockPosition: () => null,
  investigateTimeSelection: defaultTimeSelections,
  setInvestigateTimeSelection: () => null,
  highlightedTag: null,
  setHighlightedTag: () => null,
  lockedTag: null,
  setLockedTag: () => null,
};

export const InvestigateContext = createContext<InvestigateContextProps>(defaultState);

const InvestigateContextProvider: React.FC = ({ children }) => {
  const { activeViewState, updateActiveTabViewState } = useTabManager();

  const investigateView = activeViewState?.view[ViewType.INVESTIGATE];
  const [highlightedTag, setHighlightedTagState] = useState<string | null>(null);
  const lockedTag = investigateView?.lockedTag ?? null;
  const [selectedTags, setSelectedTagsState] = useState<{
    [tagName: string]: IselectedTag;
  }>({});
  const investigateTimeSelection = activeViewState?.timeSelection ?? defaultTimeSelections;

  const setHighlightedTag = useCallback((value: React.SetStateAction<string | null>) => {
    setHighlightedTagState((prev) => (typeof value === 'function' ? value(prev) : value));
  }, []);

  const setLockedTag = useCallback(
    (value: React.SetStateAction<string | null>) => {
      if (!activeViewState) return;
      const next = typeof value === 'function' ? value(lockedTag) : value;
      updateActiveTabViewState({
        ...activeViewState,
        view: {
          ...activeViewState.view,
          [ViewType.INVESTIGATE]: {
            ...investigateView,
            lockedTag: next,
          },
        },
      });
    },
    [activeViewState, investigateView, lockedTag, updateActiveTabViewState],
  );

  const setSelectedTags = useCallback(
    (value: React.SetStateAction<{ [tagName: string]: IselectedTag }>) => {
      setSelectedTagsState((prev) => (typeof value === 'function' ? value(prev) : value));
    },
    [],
  );

  const setInvestigateTimeSelection = useCallback(
    (value: React.SetStateAction<TimeSelection>) => {
      if (!activeViewState) return;
      const next = typeof value === 'function' ? value(investigateTimeSelection) : value;
      updateActiveTabViewState({
        ...activeViewState,
        timeSelection: next,
      });
    },
    [activeViewState, investigateTimeSelection, updateActiveTabViewState],
  );

  const [tagChartData, setTagChartData] = useState<{
    [tagName: string]: CRTagData<TimeSeries, Mode, AnomalyRange>;
  }>({});

  const [hoveredTagDatapoints, setHoveredTagDatapoints] = useState<TagDataPoint>({});
  const [hoverLockPosition, setHoverLockPosition] = useState<number | null>(null);

  const investigateState = useMemo(
    () => ({
      selectedTags,
      setSelectedTags,
      tagChartData,
      setTagChartData,
      investigateTimeSelection,
      setInvestigateTimeSelection,
      hoveredTagDatapoints,
      setHoveredTagDatapoints,
      highlightedTag,
      setHighlightedTag,
      lockedTag,
      setLockedTag,
      hoverLockPosition,
      setHoverLockPosition,
    }),
    [
      selectedTags,
      setSelectedTags,
      tagChartData,
      investigateTimeSelection,
      setInvestigateTimeSelection,
      hoveredTagDatapoints,
      highlightedTag,
      setHighlightedTag,
      lockedTag,
      setLockedTag,
      hoverLockPosition,
    ],
  );

  return (
    <InvestigateContext.Provider value={investigateState}>{children}</InvestigateContext.Provider>
  );
};

export default InvestigateContextProvider;
